import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {
  ConditionOperatorKey,
  ConditionOperatorKeyToText,
} from 'common/src/modules/conditions/conditions.types';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import updateFallbackOutputWithDefaultValues from '../../utils/conditions/updateFallbackOutputWithDefaultValues';

const setOperatorInputSchema = z.object({
  operator: z.nativeEnum(ConditionOperatorKey).describe('The operator to set'),
});

export default function setOperatorTool() {
  async function execute(
    params: ToolExecuteParams<typeof setOperatorInputSchema, ConditionCardScratchpadState>,
  ) {
    const {operator} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.CONDITION,
    );

    if (!validatedScratchpad.card.condKeyType) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: 'No signal selected, please set the signal first',
        }),
      );
    }

    if (!validatedScratchpad.card.optionalOperators?.some((op) => op.value === operator)) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: `The operator ${ConditionOperatorKeyToText[operator]} is not supported for the ${validatedScratchpad.card.signalDescription} signal. Supported operators are: ${validatedScratchpad.card.optionalOperators?.map((op) => ConditionOperatorKeyToText[op.value]).join(', ')}`,
        }),
      );
    }

    validatedScratchpad.card.condOperator = operator;

    // Update fallback output with default values based on the new operator
    // This is important for Between operator which requires different structure (range vs array)
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (output) => output.type === OutputType.Fallback,
    );

    if (fallbackOutput) {
      updateFallbackOutputWithDefaultValues(
        fallbackOutput,
        validatedScratchpad.card.condKeyType,
        operator,
      );
    }

    return toolResult(`Operator set successfully: ${operator}`);
  }

  return tool<typeof setOperatorInputSchema, ConditionCardScratchpadState>({
    name: V2CardAgentToolName.SetOperator,
    displayName: 'Setting operator',
    description:
      "Following the user's prompt, set the operator for the condition card. The operator must be one of the optional operators returned from the signal. Verify that the selected signal supports the suggested operator before setting it.",
    inputSchema: setOperatorInputSchema,
    execute,
  });
}
